import type {
  CamelCaseFeatures,
  CamelCaseParallelDealer,
  CamelCaseSpecifications,
} from '@/apis/server/new-car/v1/car-detail';

export const calculateAveragePrice = (items: { price: number; }[]): number => {
  if (items.length === 0) return 0;

  const total = items.reduce((sum, item) => sum + item.price, 0);
  const average = total / items.length;
  return Math.ceil(average / 100) * 100;
};

export const concatenateValidFeatures = (feature: CamelCaseFeatures, spec: CamelCaseSpecifications) => { // @suppress longLineCheck
  const valuesOfFeatures = [];

  if (feature.airbags === 1) {
    valuesOfFeatures.push('1 airbag');
  }

  if (feature.airbags > 1) {
    valuesOfFeatures.push(`${feature.airbags} airbags`);
  }

  if (feature.tractionControl) {
    valuesOfFeatures.push('Traction control');
  }

  if (feature.controlOnSteeringWheel) {
    valuesOfFeatures.push('Multi-Function steering wheel');
  }

  if (feature.pushStartEngine) {
    valuesOfFeatures.push('Keyless engine start');
  }

  if (feature.autoHeadlights) {
    valuesOfFeatures.push('Auto headlights');
  }

  if (feature.rainSensingWipers) {
    valuesOfFeatures.push('Rain sensing wipers');
  }

  if (feature.autoRetractableSideMirrors) {
    valuesOfFeatures.push('Electrical retractable side mirrors');
  }

  if (feature.paddleShifters) {
    valuesOfFeatures.push('Paddle shifters');
  }

  if (feature.cruiseControl) {
    valuesOfFeatures.push('Cruise controls');
  }

  if (feature.electricParkBrakeButton) {
    valuesOfFeatures.push('Electric park brake button');
  }

  if (feature.navigationSystem) {
    valuesOfFeatures.push('Navigation system');
  }

  if (feature.bluetoothInterface) {
    valuesOfFeatures.push('Bluetooth Interface');
  }

  if (feature.smartKey) {
    valuesOfFeatures.push('Smart key');
  }

  if (feature.remoteBootRelease) {
    valuesOfFeatures.push('Remote boot release');
  }

  if (feature.electricTailgate) {
    valuesOfFeatures.push('Electric tailgate');
  }

  if (feature.headLights && feature.headLights?.length > 0) {
    valuesOfFeatures.push(`Headlights - ${feature.headLights}`);
  }

  if (
    feature.daytimeRunningLights
    && feature.daytimeRunningLights?.length > 0
    && feature.daytimeRunningLights !== 'No'
  ) {
    valuesOfFeatures.push(`Daytime running lights - ${feature.daytimeRunningLights}`);
  }

  if (feature.frontFogLamps) {
    valuesOfFeatures.push('Front fog lamps');
  }

  if (feature.sportsRims) {
    if (spec.rimSize && spec.rimSize > 0) {
      valuesOfFeatures.push(`${spec.rimSize} Rims`);
    } else {
      valuesOfFeatures.push('Rims');
    }
  }

  if (feature.reverseSensors) {
    valuesOfFeatures.push('Reverse sensors');
  }

  if (feature.sideMirrorIndicators) {
    valuesOfFeatures.push('Side mirror indicators');
  }

  if (feature.sunroofMoonroofSkyroof) {
    valuesOfFeatures.push('Sunroof/Moonroof/Panoramic roof');
  }

  if (feature.climateControlAircon) {
    valuesOfFeatures.push('Auto climate control aircon');
  }

  if (feature.dualZoneAc) {
    valuesOfFeatures.push('Multi-zone aircon');
  }

  if (feature.rearAircon) {
    valuesOfFeatures.push('Rear aircon');
  }

  if (feature.reverseCamera) {
    valuesOfFeatures.push('Reverse camera');
  }

  if (feature.audioAuxUSBPort) {
    valuesOfFeatures.push('Audio Aux/USB Port');
  }

  if (feature.driverElectricSeat) {
    valuesOfFeatures.push('Driver\'s electric seat');
  }

  if (feature.passengerElectricSeat) {
    valuesOfFeatures.push('Passenger\'s electric seat');
  }

  if (feature.memorySeats) {
    valuesOfFeatures.push('Driver\'s memory seat');
  }

  if (feature.memorySeatsPassenger) {
    valuesOfFeatures.push('Passenger\'s memory seat');
  }

  if (feature.knockdownRearSeats) {
    valuesOfFeatures.push('Knockdown rear seats');
  }

  if (feature.leatherSeats) {
    valuesOfFeatures.push('Leather seats');
  }

  if (feature.leatherSteeringWheel) {
    valuesOfFeatures.push('Leather steering wheel');
  }

  const stringOfFeatures = valuesOfFeatures.join(', ');

  return stringOfFeatures;
};

export const parseHtmlStringIntoArray = (htmlString: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const listItems = doc.querySelectorAll('li');

  return Array.from(listItems).map(item => item.textContent?.trim());
};

export const filterDealersBySubModelId = (dealers: CamelCaseParallelDealer[], subModelId: number) => dealers.filter( // @suppress longLineCheck
  dealer => dealer.adverts.some(advert => advert.subModelId === subModelId),
);

export const formatPhone = (phone: string) => `${phone.slice(0, 4)} ${phone.slice(4)}`;
